import { Int } from '@adornis/baseql/baseqlTypes.js';
import { registerSubscription } from '@adornis/baseql/metadata/register.js';
import { getEntityByID } from '@adornis/baseql/operations/mongo.js';
import { getCollectionHandle } from '@adornis/baseql/server/collections.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { from, switchMap } from 'rxjs';
import { PresenceRecord, SchuKoPresenceRecord } from '../../../PresenceRecord.js';
import { KPF } from '../entities/KPF.js';

export const subscribeKPFByAttendance = registerSubscription({
  type: () => [KPF],
  operationName: 'subscribeKPFByAttendance',
  params: [
    { type: () => Int, name: 'skip' },
    { type: () => Int, name: 'limit' },
    { type: () => String, name: 'userID' },
  ],
  resolve: (skip: number, limit: number, userID: string) => {
    return (gqlFields: BaseQLSelectionSet<KPF>) => {
      const handle = getCollectionHandle<PresenceRecord>(PresenceRecord._collectionName);
      return from(publishFilter(PresenceRecord._class)).pipe(
        switchMap(publishFilter => {
          const filter = {
            $or: [
              { 'studentRecordsUS.userID': userID },
              { 'studentRecordsOS.userID': userID },
              { 'lekoRecords.userID': userID },
              { 'schukoRecords.userID': userID },
            ],
          };
          const selector = publishFilter ? { $and: [publishFilter, filter] } : filter;
          const options = {
            projection: { _class: 1, _id: 1 },
          };
          return handle.watchQuery(selector, options);
        }),
        // eslint-disable-next-line complexity
        switchMap(async records => {
          const normalRecordsIDs = records.filter(r => r._class === PresenceRecord._class).map(r => r._id);
          const schuKoRecordsIDs = records.filter(r => r._class === SchuKoPresenceRecord._class).map(r => r._id);

          const normalRecords = await getEntityByID(
            PresenceRecord,
            normalRecordsIDs,
          )({
            course: {
              kpfID: 1,
            },
          });
          const schuKoRecords = await getEntityByID(
            SchuKoPresenceRecord,
            schuKoRecordsIDs,
          )({
            kpfID: 1,
          });

          return (await loadMongoData(KPF, [
            ...new Set([...normalRecords.map(r => r.course?.kpfID), ...schuKoRecords.map(r => r.kpfID)]),
          ])) as KPF[];
        }),
      );
    };
  },
});
